import React from 'react';
import { Stack, Typography } from '@mui/material';
import { EmptyState, ListTable, Panel, SearchField, StatusChip, type ListColumn } from '../../../ui-kit';
import { fetchWaConversations, type WaCard } from '../../../services/websiteAssistantService';
import { fromNow, matches, useLive } from '../rocco/live';
import ChatStoryModal from './ChatStory';
import { intentOf, stateChip } from './status';

type Scope = 'live' | 'completed';

/** Chats going on now. When one is over it moves to Completed chats. */
export function LiveChatsPage() {
  return <ChatsPage scope="live" />;
}

/** Every chat that is over, newest first: quote requests and clients' questions together. */
export function CompletedChatsPage() {
  return <ChatsPage scope="completed" />;
}

const subtitle = (scope: Scope, n: number): string => (scope === 'live'
  ? `${n} visitor${n === 1 ? ' is' : 's are'} chatting now · a chat moves to Completed chats when it is over`
  : `${n} chat${n === 1 ? '' : 's'} · auto quote requests sent to AgencyZoom, and clients' questions about their policy`);

const EMPTY: Record<Scope, string> = {
  live: 'Nobody is chatting right now. When a visitor opens the chat on the website, it shows here.',
  completed: 'No finished chats yet. When a visitor finishes a quote request or a question, it shows here.',
};

function ChatsPage({ scope }: { scope: Scope }) {
  const fetcher = React.useCallback(() => fetchWaConversations(scope), [scope]);
  const { data, error, loading } = useLive(fetcher, 8000);
  const [open, setOpen] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const live = scope === 'live';

  const all = data?.conversations ?? [];
  const rows = all.filter((c) => matches(query, [c.visitor, c.email, c.cars, c.policy_num, c.question]));

  const result = (c: WaCard): string => {
    if (c.source === 'seed') return 'Example';
    if (c.needs_sending_again && c.state !== 'lead_failed') return 'Not all in AgencyZoom yet: open it to send again';
    if (c.state === 'lead_sent') return `Lead ${c.lead_id}${c.assigned_to ? ` · ${c.assigned_to}` : ''}`;
    if (c.state === 'lead_failed') return 'Not sent yet';
    if (c.state === 'handed') {
      return `${c.handed_reason || 'Passed to the team'}${c.assigned_to ? ` · call-back for ${c.assigned_to}` : ''}`;
    }
    if (c.state === 'answered') return 'Answered from the policy';
    return c.waiting_on ? `Waiting on: ${c.waiting_on}` : '';
  };

  const columns: ListColumn<WaCard>[] = [
    {
      key: 'visitor', header: 'Visitor', grow: 1.1, minWidth: 160, sortValue: (c) => c.visitor ?? '',
      render: (c) => (
        <Stack spacing={0}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{c.visitor || 'A visitor'}</Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary, overflowWrap: 'anywhere' })}>
            {c.email || (c.policy_num ? `Policy ${c.policy_num}` : '')}
          </Typography>
        </Stack>
      ),
    },
    {
      key: 'started', header: 'Started', width: 120, sortValue: (c) => c.started_at, sortStartsDescending: true,
      render: (c) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{fromNow(c.started_at)}</Typography>,
    },
    {
      key: 'about', header: 'About', width: 190, sortValue: (c) => c.intent ?? '',
      render: (c) => (
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
          {c.intent === 'quote' ? 'Auto quote request' : c.intent === 'question' ? 'Question about a policy'
            : live ? intentOf(c.intent) : 'Asked for a person'}
        </Typography>
      ),
    },
    {
      key: 'detail', header: 'Details', grow: 1.4, minWidth: 200,
      render: (c) => (
        <Stack spacing={0}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary, overflowWrap: 'anywhere' })}>
            {(c.intent === 'quote' ? c.cars : c.question) || '—'}
          </Typography>
          {c.intent === 'quote' && c.files ? (
            <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
              {c.files} document{c.files === 1 ? '' : 's'} read
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      key: 'state', header: 'Where it stands', grow: 1.3, minWidth: 220, sortValue: (c) => c.state,
      render: (c) => (
        <Stack spacing={0.5} alignItems="flex-start">
          <StatusChip {...stateChip(c.state)} />
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>{result(c)}</Typography>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Panel
        title={live ? 'Live chats' : 'Completed chats'}
        subtitle={subtitle(scope, all.length)}
        search={<SearchField value={query} onChange={setQuery} placeholder="Search chats…" />}
      >
        {error && !data ? <EmptyState message={error} /> : (
          <ListTable columns={columns} rows={rows} getRowKey={(c) => c.id} loading={loading} pageSize={25}
            onRowClick={(c) => setOpen(c.id)}
            emptyMessage={all.length ? 'Nothing matches. Clear the search.' : EMPTY[scope]} />
        )}
      </Panel>
      {open ? <ChatStoryModal chatId={open} onClose={() => setOpen(null)} /> : null}
    </>
  );
}
